import type { OffersEmbedResolvedData } from "../schemas/upsell-constructs";
import { computeUpsellPrice } from "./upsell-pricing";

// Structural shape of the per-tier styles prop in `@codway-dev/ui`.
// Defined here (instead of importing) so `@codway-dev/shared` stays free
// of UI-package dependencies — both admin and widget spread the result
// into <QuantityTiersField /> and TypeScript matches structurally.
export interface QuantityTierEmbedStyles {
  borderColor?: string;
  activeBorderColor?: string;
}

export interface QuantityTierDisplayProps {
  id: string;
  quantity: number;
  title: string;
  priceLabel: string;
  // Only present when the tier has a real discount and hide_compare_at_price is false.
  compareAtLabel?: string;
  badge?: {
    text: string;
    textColor?: string;
    backgroundColor?: string;
  };
  imageUrl?: string;
  styles?: QuantityTierEmbedStyles;
  isDefault: boolean;
  priceColor?: string;
  titleColor?: string;
}

export interface QuantityTiersEmbedFieldProps {
  tiers: QuantityTierDisplayProps[];
  productTitle: string;
  layout: "horizontal" | "grid";
  variants?: { id: string; title: string }[];
}

export function mapQuantityTiersResolvedDataToFieldProps(
  resolvedData: OffersEmbedResolvedData,
  formatPrice: (amount: number) => string
): QuantityTiersEmbedFieldProps | null {
  const dd = resolvedData.display_data;
  if (dd.component_type !== "quantity_tiers") return null;

  const target = resolvedData.product_target;
  const productTitle = target.display_meta?.title ?? "";
  const fallbackImageUrl = target.display_meta?.image_url;
  // display_meta.price is stored as a string in the schema.
  const basePrice = parseFloat(target.display_meta?.price ?? "0");
  // When `use_compare_at_price_as_base` is on, the strikethrough uses the
  // product's compare-at price (e.g. MSRP) instead of the discount-derived
  // base. Discounts still compute off the real price — this only affects
  // what we cross out. Falls back to `basePrice` if no compare-at exists.
  const compareAtPrice = dd.use_compare_at_price_as_base
    ? parseFloat(target.display_meta?.compare_at_price ?? "") || basePrice
    : basePrice;
  const variants = target.display_meta?.variants;

  const tiers: QuantityTierDisplayProps[] = dd.tiers.map((tier, index) => {
    const unitPrice = computeUpsellPrice(tier.pricing_logic, basePrice);
    const priceLabel = formatPrice(unitPrice * tier.quantity);
    // Show the original price when either the tier offers a real discount,
    // or `use_compare_at_price_as_base` provides a higher MSRP to cross out.
    // The merchant can suppress it per-tier with `hide_compare_at_price`.
    const compareAtLabel =
      !tier.hide_compare_at_price && unitPrice < compareAtPrice
        ? formatPrice(compareAtPrice * tier.quantity)
        : undefined;

    const badge = tier.badge
      ? {
          text: tier.badge.text,
          textColor: tier.badge.text_color,
          backgroundColor: tier.badge.background_color,
        }
      : undefined;

    const styles = tier.styles
      ? {
          borderColor: tier.styles.border_color,
          activeBorderColor: tier.styles.active_border_color,
        }
      : undefined;

    return {
      id: `tier-${index}`,
      quantity: tier.quantity,
      title: tier.title,
      priceLabel,
      compareAtLabel,
      badge,
      // Tier-level image takes precedence; falls back to the product's image.
      imageUrl: tier.custom_image_url ?? fallbackImageUrl,
      styles,
      isDefault: tier.is_default,
      priceColor: tier.price_color,
      titleColor: tier.title_color,
    };
  });

  return {
    tiers,
    productTitle,
    layout: dd.layout,
    ...(variants && variants.length > 0 ? { variants } : {}),
  };
}
